import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { barAndBeat, eventIndexAt, type Timing } from '../../domain'
import {
  audioTimeOfEvent,
  peekAudioContext,
  perceivedDelay,
  setPlaybackSession,
  unlockAudio,
} from '../../platform/audio'
import { browserTimers } from '../../transport/clock'
import { TransportEngine } from '../../transport/engine'
import { scheduleClick } from '../../transport/metronome'
import type { Loop, Phase, Position, TransportState } from '../../transport/transport'
import { useDeviceSettings } from '../device'

/**
 * La guía sincronizada en una pantalla: el motor del transporte, el metrónomo y la posición. La
 * posición no se guarda en React: se calcula del reloj de audio en cada fotograma y solo provoca
 * un repintado cuando cambia el acorde o la cuenta (regla 2; el iPad Air 2 lo agradece).
 */
export interface TransportOptions {
  timing: Timing
  beatsPerBar: number
  bpm: number
  /** Pulso donde está el cursor al abrir (para volver al mismo acorde tras un cierre). */
  initialBeat: number
  metronome: boolean
  /** Elemento donde se escribe «compás · pulso» en cada pulso, sin pasar por React. */
  beatDisplay?: React.RefObject<HTMLElement | null>
  onEnded?: () => void
}

export interface LiveTransport {
  state: TransportState
  phase: Phase
  /** Pulso del cursor (parado o en pausa) o el último pintado (sonando). */
  beat: number
  /** Evento del mapa de tiempos que suena (o donde está el cursor). */
  index: number
  countInLeft: number
  start(options: { from?: number; countIn: number }): void
  pause(): void
  stop(): void
  seek(beat: number): void
  setBpm(bpm: number): void
  setLoop(loop: Loop | null): void
  /** Pulso de la canción en que ocurrió un toque o una tecla, descontando la latencia. */
  beatOfEvent(timeStamp: number): number | null
}

interface Frame {
  phase: Phase
  index: number
  countInLeft: number
  beat: number
}

function paintBeat(element: HTMLElement | null | undefined, position: Position, perBar: number) {
  if (!element) return
  if (position.phase === 'countIn') {
    element.textContent = position.countInLeft > 0 ? `${position.countInLeft}…` : '…'
    return
  }
  const { bar, beat } = barAndBeat(Math.max(0, position.beat), perBar)
  element.textContent = `${bar} · ${beat}`
}

export function useTransport(options: TransportOptions): LiveTransport {
  const { timing, beatsPerBar, metronome, beatDisplay } = options
  const { latencyMs } = useDeviceSettings()
  const latency = useRef(latencyMs)
  const timingRef = useRef(timing)
  const onEnded = useRef(options.onEnded)
  useEffect(() => {
    latency.current = latencyMs
    timingRef.current = timing
    onEnded.current = options.onEnded
  })

  const [engine] = useState(() => {
    const created = new TransportEngine({
      clock: { now: () => peekAudioContext()?.currentTime ?? 0 },
      timer: browserTimers,
      click: ({ time, accent }) => {
        const ctx = peekAudioContext()
        return ctx ? scheduleClick(ctx, ctx.destination, time, { accent }) : null
      },
      bpm: options.bpm,
      beatsPerBar,
      total: timing.total,
    })
    created.seek(options.initialBeat)
    return created
  })

  const subscribe = useCallback(
    (listener: () => void) =>
      engine.subscribe((event) => {
        if (event === 'ended') onEnded.current?.()
        listener()
      }),
    [engine],
  )
  const state = useSyncExternalStore(subscribe, () => engine.getState())

  useEffect(() => () => engine.dispose(), [engine])
  useEffect(() => {
    engine.configure({ beatsPerBar, total: timing.total })
  }, [engine, beatsPerBar, timing.total])
  useEffect(() => {
    engine.setMetronome(metronome)
  }, [engine, metronome])

  /** El instante que se está oyendo: el reloj de audio menos el retraso hasta el oído. */
  const heardNow = useCallback(() => {
    const ctx = peekAudioContext()
    if (!ctx) return 0
    return ctx.currentTime - perceivedDelay(ctx, latency.current)
  }, [])

  const running = state.status === 'running'
  const [frame, setFrame] = useState<Frame | null>(null)

  // Cada fotograma: posición a partir del reloj. Solo se avisa a React si cambia algo visible.
  useEffect(() => {
    if (!running) return
    let handle = 0
    let last: Frame | null = null
    let lastWhole = Number.NaN
    const draw = () => {
      const position = engine.position(heardNow())
      const index = eventIndexAt(timingRef.current, position.beat)
      if (
        !last ||
        last.phase !== position.phase ||
        last.index !== index ||
        last.countInLeft !== position.countInLeft
      ) {
        last = {
          phase: position.phase,
          index,
          countInLeft: position.countInLeft,
          beat: position.beat,
        }
        setFrame(last)
      }
      const whole =
        position.phase === 'countIn' ? -position.countInLeft : Math.floor(position.beat + 1e-6)
      if (whole !== lastWhole) {
        lastWhole = whole
        paintBeat(beatDisplay?.current, position, beatsPerBar)
      }
      handle = requestAnimationFrame(draw)
    }
    handle = requestAnimationFrame(draw)
    return () => cancelAnimationFrame(handle)
  }, [engine, running, state, heardNow, beatDisplay, beatsPerBar])

  // Parado o en pausa, el contador muestra dónde está el cursor.
  useEffect(() => {
    if (running) return
    paintBeat(beatDisplay?.current, engine.position(0), beatsPerBar)
  }, [engine, running, state, beatDisplay, beatsPerBar])

  // Si la app pasa a segundo plano o iOS suspende el audio, se pausa de forma explícita (§5.2).
  useEffect(() => {
    if (!running) return
    const ctx = peekAudioContext()
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        console.info('Guía en pausa: la app pasó a segundo plano')
        engine.pause(heardNow())
      }
    }
    const onAudioState = () => {
      if (ctx && ctx.state !== 'running') {
        console.info(`Guía en pausa: audio ${ctx.state}`)
        engine.pause(heardNow())
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    ctx?.addEventListener('statechange', onAudioState)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      ctx?.removeEventListener('statechange', onAudioState)
    }
  }, [engine, running, heardNow])

  // Al parar, se anota cómo fue (se ve en la consola de depuración) y se suelta la sesión de audio.
  const wasRunning = useRef(false)
  useEffect(() => {
    if (running) {
      wasRunning.current = true
      return
    }
    if (!wasRunning.current) return
    wasRunning.current = false
    const stats = engine.getStats()
    console.info(
      `Guía: ${stats.scheduled} clics, ${stats.missed} saltados, antelación mínima ${Math.round(
        stats.minLead * 1000,
      )} ms, mayor hueco ${Math.round(stats.maxTickGap * 1000)} ms`,
    )
    void setPlaybackSession(false)
  }, [engine, running])

  const start = useCallback(
    ({ from, countIn }: { from?: number; countIn: number }) => {
      // Todo lo que exige un toque se lanza antes de esperar a nada (iOS).
      const unlocking = unlockAudio()
      setFrame(null)
      void setPlaybackSession(true)
      unlocking.then(
        () => engine.play({ ...(from === undefined ? {} : { from }), countIn }),
        (error: unknown) => console.warn('No se pudo activar el audio', error),
      )
    },
    [engine],
  )

  const beatOfEvent = useCallback(
    (timeStamp: number) => {
      const ctx = peekAudioContext()
      if (!ctx || engine.getState().status !== 'running') return null
      const time = audioTimeOfEvent(ctx, timeStamp) - perceivedDelay(ctx, latency.current)
      const position = engine.position(time)
      return position.phase === 'playing' ? position.beat : null
    },
    [engine],
  )

  let shown: Frame
  if (state.status === 'running') {
    // Hasta el primer fotograma, el pulso en que se va a entrar.
    shown = frame ?? {
      phase: 'countIn',
      index: eventIndexAt(timing, state.run.beat),
      countInLeft: state.run.countIn,
      beat: state.run.beat,
    }
  } else {
    shown = {
      phase: state.status,
      index: eventIndexAt(timing, state.cursor),
      countInLeft: 0,
      beat: state.cursor,
    }
  }

  return {
    state,
    phase: shown.phase,
    beat: shown.beat,
    index: shown.index,
    countInLeft: shown.countInLeft,
    start,
    pause: useCallback(() => engine.pause(heardNow()), [engine, heardNow]),
    stop: useCallback(() => engine.stop(), [engine]),
    seek: useCallback((beat: number) => engine.seek(beat), [engine]),
    setBpm: useCallback((bpm: number) => engine.setBpm(bpm), [engine]),
    setLoop: useCallback((loop: Loop | null) => engine.setLoop(loop), [engine]),
    beatOfEvent,
  }
}
